<template>
  <div class="dx-picker">
    <!-- Chosen -->
    <div class="dx-chosen" v-if="value.length">
      <span v-for="o in value" :key="o.key" class="dx-chip" :class="`dx-chip--${o.source}`" :title="hint(o)">
        {{ o.name }}<span v-if="o.diagnosis_code" class="dx-code">{{ o.diagnosis_code }}</span>
        <button type="button" class="dx-chip-x" @click="toggle(o)" aria-label="Remove">&times;</button>
      </span>
      <button type="button" class="btn btn-link btn-sm p-0 ml-1" @click="$emit('input', [])">Clear all</button>
    </div>
    <div v-else-if="loadingChosen" class="small text-muted mb-2"><i class="mdi mdi-loading mdi-spin mr-1"></i>Loading the chosen diagnoses…</div>

    <!-- Search -->
    <div class="input-group input-group-sm">
      <div class="input-group-prepend"><span class="input-group-text"><i class="fe fe-search"></i></span></div>
      <input class="form-control" v-model="q" @input="search" placeholder="Search by name or ICD code: standard list and what your facilities typed" />
      <div class="input-group-append" v-if="q"><button class="btn btn-outline-secondary" type="button" @click="q = ''; search()">&times;</button></div>
    </div>

    <!-- Results: tick as many as needed, the list stays -->
    <div class="dx-results">
      <div class="dx-results-head small text-muted">
        <span>{{ q ? `Matches for “${q}”` : "Most used by your facilities" }}<i v-if="loading" class="mdi mdi-loading mdi-spin ml-1"></i></span>
        <button v-if="unticked.length > 1" type="button" class="btn btn-link btn-sm p-0" @click="tickAll">Tick all {{ unticked.length }} shown</button>
      </div>
      <div v-if="!loading && !options.length" class="small text-muted p-2">No diagnosis found{{ q ? ` for “${q}”` : "" }}.</div>
      <label v-for="o in options" :key="o.key" class="dx-row" :class="{ 'dx-row--on': isChosen(o) }">
        <input type="checkbox" :checked="isChosen(o)" @change="toggle(o)" />
        <span class="dx-name">{{ o.name }}<span v-if="o.diagnosis_code" class="dx-code">{{ o.diagnosis_code }}</span></span>
        <span class="dx-source" :class="`dx-source--${o.source}`">{{ o.source === "standard" ? "ICD" : "Entered" }}</span>
        <span class="dx-uses">{{ usesLabel(o) }}</span>
      </label>
    </div>

    <!-- Related -->
    <div v-if="related.length" class="mt-2 small">
      <span class="text-muted mr-1">Related:</span>
      <button v-for="o in related" :key="o.key" type="button" class="btn btn-xs btn-outline-primary mr-1 mb-1 py-0" @click="toggle(o)" :title="hint(o)">
        + {{ o.name }} <span class="text-muted">({{ o.uses }})</span>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Diagnoses for a tracked disease (GET diseases/diagnoses, diseases/similar). Choices are standard (ICD) diagnoses,
 * and diagnoses entered by the agency or typed by facilities, one per name however many records carry it.
 * v-model: the chosen choices ({ key, id, ids, name, diagnosis_code, source, uses, last_used, records }).
 */
export default {
  props: {
    value: { type: Array, default: () => [] },
    loadingChosen: { type: Boolean, default: false },
  },
  data() {
    return { q: "", options: [], related: [], loading: false, timer: null, seq: 0, relatedSeq: 0 };
  },
  computed: {
    chosenKeys() { return new Set(this.value.map((o) => o.key)); },
    unticked() { return this.options.filter((o) => !this.chosenKeys.has(o.key)); },
  },
  watch: {
    value() { this.loadRelated(); },
  },
  mounted() {
    this.fetch();
    this.loadRelated();
  },
  methods: {
    search() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.fetch, 250);
    },
    fetch() {
      const seq = ++this.seq; // only the latest answer is shown
      this.loading = true;
      this.axios.get("/api/v1/auth/diseases/diagnoses", { params: { q: this.q } })
        .then((res) => { if (seq === this.seq) this.options = res.data.data; })
        .catch(() => {})
        .finally(() => { if (seq === this.seq) this.loading = false; });
    },
    loadRelated() {
      const ids = this.value.flatMap((o) => o.ids);
      if (!ids.length) { this.related = []; return; }
      const seq = ++this.relatedSeq;
      this.axios.get("/api/v1/auth/diseases/similar", { params: { ids } })
        .then((res) => { if (seq === this.relatedSeq) this.related = res.data.data.filter((o) => !this.chosenKeys.has(o.key)); })
        .catch(() => {});
    },
    isChosen(o) { return this.chosenKeys.has(o.key); },
    toggle(o) {
      this.$emit("input", this.isChosen(o) ? this.value.filter((x) => x.key !== o.key) : this.value.concat([o]));
    },
    tickAll() { this.$emit("input", this.value.concat(this.unticked)); },
    usesLabel(o) {
      if (!o.uses) return "not used yet";
      return `used ${o.uses.toLocaleString()}×` + (o.last_used ? ` · last ${o.last_used}` : "");
    },
    hint(o) {
      const what = o.source === "standard" ? "Standard (ICD) diagnosis" : `Entered by your agency or facilities${o.records > 1 ? `, saved under ${o.records} records` : ""}; new records with this name count too`;
      return `${what}. ${this.usesLabel(o)}`;
    },
  },
};
</script>

<style scoped>
.dx-chosen { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 8px; }
.dx-chip { display: inline-flex; align-items: center; gap: 4px; background: #eff6ff; color: #1e3a8a; border: 1px solid #bfdbfe; border-radius: 999px; padding: 2px 4px 2px 10px; font-size: 0.8rem; font-weight: 600; }
.dx-chip--agency { background: #f5f3ff; color: #5b21b6; border-color: #ddd6fe; }
.dx-chip-x { border: 0; background: transparent; color: inherit; font-size: 1rem; line-height: 1; padding: 0 4px; cursor: pointer; opacity: 0.7; }
.dx-chip-x:hover { opacity: 1; }
.dx-code { color: #64748b; font-weight: 500; font-size: 0.75rem; margin-left: 4px; }
.dx-results { border: 1px solid #e2e8f0; border-radius: 8px; margin-top: 6px; max-height: 280px; overflow-y: auto; background: #fff; }
.dx-results-head { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; border-bottom: 1px solid #f1f5f9; position: sticky; top: 0; background: #f8fafc; z-index: 1; }
.dx-row { display: flex; align-items: center; gap: 8px; padding: 6px 10px; margin: 0; cursor: pointer; border-bottom: 1px solid #f8fafc; font-size: 0.85rem; }
.dx-row:hover { background: #f8fafc; }
.dx-row--on { background: #eff6ff; }
.dx-row input { flex-shrink: 0; }
.dx-name { flex: 1; min-width: 0; }
.dx-source { font-size: 0.68rem; font-weight: 700; border-radius: 4px; padding: 1px 6px; text-transform: uppercase; letter-spacing: 0.02em; }
.dx-source--standard { background: #e0f2fe; color: #075985; }
.dx-source--agency { background: #ede9fe; color: #5b21b6; }
.dx-uses { color: #64748b; font-size: 0.75rem; white-space: nowrap; }
.btn-xs { font-size: 0.75rem; }
@media (max-width: 576px) { .dx-uses { display: none; } }
</style>
